import { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Filter,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Download,
  MapPin,
  FileCheck,
  X,
  Building2,
  Shield,
  User,
  ArrowLeft,
} from "lucide-react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from "date-fns";
import claimsService from "../../../api/ClaimsService";
import { toast } from "react-toastify";
import Loader from "../../../components/misc/Loader";

const StatusBadge = ({ status }) => {
  const configs = {
    pending: {
      icon: Clock,
      color: "bg-yellow-100 text-yellow-800 border-yellow-200",
      label: "Pending",
    },
    received: {
      icon: FileCheck,
      color: "bg-blue-100 text-blue-800 border-blue-200",
      label: "Received",
    },
    reviewing: {
      icon: AlertCircle,
      color: "bg-indigo-100 text-indigo-800 border-indigo-200",
      label: "Reviewing",
    },
    claimed: {
      icon: CheckCircle,
      color: "bg-green-100 text-green-800 border-green-200",
      label: "Claimed",
    },
    rejected: {
      icon: XCircle,
      color: "bg-red-100 text-red-800 border-red-200",
      label: "Rejected",
    },
  };

  const config = configs[status] || configs.pending;
  const Icon = config.icon;

  return (
    <motion.span
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${config.color}`}
    >
      <Icon className="mr-1 w-3 h-3" />
      {config.label}
    </motion.span>
  );
};

const EmptyState = ({ icon: Icon, title, description }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    className="flex flex-col items-center justify-center py-16"
  >
    <div className="mb-4 p-4 bg-gray-100 rounded-full">
      <Icon className="w-12 h-12 text-gray-400" />
    </div>
    <h3 className="mb-2 text-lg font-semibold text-gray-900">{title}</h3>
    <p className="text-center text-gray-500 max-w-md">{description}</p>
  </motion.div>
);

const ClaimDetailsModal = ({ isOpen, onClose, claim }) => {
  if (!claim) return null;

  const incident = claim.incident?.[0] || {};

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={onClose}
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl"
            >
              {/* Header */}
              <div className="sticky top-0 z-10 flex items-center justify-between p-6 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-2xl">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Claim Details</h2>
                    <p className="text-sm text-blue-100">Claim ID: {claim.slug?.slice(0, 8).toUpperCase()}...</p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {/* Status & Basic Info */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <p className="text-sm font-medium text-gray-600 mb-2">Status</p>
                    <StatusBadge status={claim.claim_status} />
                  </div>
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <p className="text-sm font-medium text-gray-600 mb-2">Policy Slug</p>
                    <p className="text-sm font-mono font-semibold text-gray-900">{claim.policy_slug || "N/A"}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <p className="text-sm font-medium text-gray-600 mb-2">Provider</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {claim.insurance_provider?.company_name || claim.insurance_provider_slug || "N/A"}
                    </p>
                  </div>
                </div>

                {/* Provider Information */}
                {claim.insurance_provider && (
                  <div className="p-5 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                    <h3 className="flex items-center mb-4 text-lg font-bold text-gray-900">
                      <Building2 className="mr-2 w-5 h-5 text-purple-600" />
                      Provider Information
                    </h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="md:col-span-2 flex items-center">
                        {claim.insurance_provider.company_logo && (
                          <img 
                            src={claim.insurance_provider.company_logo} 
                            alt={claim.insurance_provider.company_name}
                            className="mr-3 w-12 h-12 rounded object-cover"
                          />
                        )}
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{claim.insurance_provider.company_name || "N/A"}</p>
                          {claim.insurance_provider.location && (
                            <p className="text-xs text-gray-600">{claim.insurance_provider.location}, {claim.insurance_provider.region}</p>
                          )}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Email</p>
                        <p className="text-sm font-semibold text-gray-900">{claim.insurance_provider.email || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Phone Number</p>
                        <p className="text-sm font-semibold text-gray-900">{claim.insurance_provider.phone_number || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Contact Person</p>
                        <p className="text-sm font-semibold text-gray-900">{claim.insurance_provider.contact_person || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">NIC Number</p>
                        <p className="text-sm font-semibold text-gray-900">{claim.insurance_provider.nic_number || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Status</p>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${
                          claim.insurance_provider.status === 1 
                            ? "bg-green-100 text-green-800" 
                            : "bg-gray-100 text-gray-800"
                        }`}>
                          {claim.insurance_provider.status === 1 ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* User Information */}
                {claim.user && (
                  <div className="p-5 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-100">
                    <h3 className="flex items-center mb-4 text-lg font-bold text-gray-900">
                      <User className="mr-2 w-5 h-5 text-green-600" />
                      User Information
                    </h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Full Name</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {claim.user.first_name} {claim.user.last_name}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Email</p>
                        <p className="text-sm font-semibold text-gray-900">{claim.user.email || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Phone Number</p>
                        <p className="text-sm font-semibold text-gray-900">{claim.user.phone_number || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Location</p>
                        <p className="text-sm font-semibold text-gray-900">{claim.user.location || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Region</p>
                        <p className="text-sm font-semibold text-gray-900">{claim.user.region || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Gender</p>
                        <p className="text-sm font-semibold text-gray-900 capitalize">{claim.user.gender || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">User Slug</p>
                        <p className="text-sm font-mono text-gray-700">{claim.user.slug || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">For Self</p>
                        <p className="text-sm font-semibold text-gray-900">{claim.for_self ? "Yes" : "No"}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Incident Details */}
                <div className="p-5 bg-gradient-to-br from-red-50 to-orange-50 rounded-xl border border-red-100">
                  <h3 className="flex items-center mb-4 text-lg font-bold text-gray-900">
                    <AlertCircle className="mr-2 w-5 h-5 text-red-600" />
                    Incident Information
                  </h3>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <p className="text-xs font-medium text-gray-600 mb-1">Date</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {incident.date ? format(parseISO(incident.date), "MMM dd, yyyy") : "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-600 mb-1">Type</p>
                      <p className="text-sm font-semibold text-gray-900">{incident.type || "N/A"}</p>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-xs font-medium text-gray-600 mb-1">Location</p>
                      <p className="flex items-center text-sm font-semibold text-gray-900">
                        <MapPin className="mr-1 w-4 h-4" />
                        {incident.location || "N/A"}
                      </p>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-xs font-medium text-gray-600 mb-1">Description</p>
                      <p className="text-sm text-gray-700">{incident.description || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-600 mb-1">Authorities Notified</p>
                      <p className="text-sm font-semibold text-gray-900 capitalize">{incident.authorities || "N/A"}</p>
                    </div>
                  </div>
                </div>

                {/* Policy Information */}
                {claim.policy && (
                  <div className="p-5 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                    <h3 className="flex items-center mb-4 text-lg font-bold text-gray-900">
                      <Shield className="mr-2 w-5 h-5 text-blue-600" />
                      Policy Information
                    </h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Policy Name</p>
                        <p className="text-sm font-semibold text-gray-900">{claim.policy.policy_name || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Policy Type</p>
                        <p className="text-sm font-semibold text-gray-900 capitalize">
                          {claim.policy.policy_type?.replace(/_/g, " ") || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Geography</p>
                        <p className="text-sm font-semibold text-gray-900 capitalize">
                          {claim.policy.geography || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Risk Level</p>
                        <p className="text-sm font-semibold text-gray-900 capitalize">
                          {claim.policy.risk_level || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Status</p>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${
                          claim.policy.status === "approved" 
                            ? "bg-green-100 text-green-800" 
                            : "bg-yellow-100 text-yellow-800"
                        }`}>
                          {claim.policy.status || "N/A"}
                        </span>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Max Claims</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {claim.policy.max_claims || "N/A"}
                        </p>
                      </div>
                      <div className="md:col-span-2">
                        <p className="text-xs font-medium text-gray-600 mb-1">Description</p>
                        <p className="text-sm text-gray-700">{claim.policy.description || "N/A"}</p>
                      </div>
                      {claim.policy.short_description && (
                        <div className="md:col-span-2">
                          <p className="text-xs font-medium text-gray-600 mb-1">Short Description</p>
                          <p className="text-sm text-gray-700">{claim.policy.short_description}</p>
                        </div>
                      )}
                      {claim.policy.terms && (
                        <div className="md:col-span-2">
                          <p className="text-xs font-medium text-gray-600 mb-1">Terms</p>
                          <p className="text-sm text-gray-700">{claim.policy.terms}</p>
                        </div>
                      )}
                      {claim.policy.policy_rules && (
                        <div className="md:col-span-2">
                          <p className="text-xs font-medium text-gray-600 mb-1">Policy Rules</p>
                          <p className="text-sm text-gray-700">{claim.policy.policy_rules}</p>
                        </div>
                      )}
                      {claim.policy.support_contact && (
                        <div className="md:col-span-2">
                          <p className="text-xs font-medium text-gray-600 mb-1">Support Contact</p>
                          <p className="text-sm text-gray-700">{claim.policy.support_contact}</p>
                        </div>
                      )}
                      {claim.policy.reject_reason && (
                        <div className="md:col-span-2">
                          <p className="text-xs font-medium text-gray-600 mb-1">Reject Reason</p>
                          <p className="text-sm text-red-700 bg-red-50 p-2 rounded">{claim.policy.reject_reason}</p>
                        </div>
                      )}
                    </div>

                    {/* Coverage Durations/Packages - Different structures for different policy types */}
                    {claim.policy.coverage_durations && claim.policy.coverage_durations.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-blue-200">
                        <p className="text-xs font-medium text-gray-600 mb-3">
                          {claim.policy.policy_type === "intercity_travel" ? "Coverage Durations" : "Available Packages"}
                        </p>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                          {claim.policy.coverage_durations.map((item, idx) => {
                            // Intercity Travel Structure
                            if (claim.policy.policy_type === "intercity_travel") {
                              return (
                                <div key={idx} className="p-3 bg-white rounded-lg border border-gray-200">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-semibold text-gray-900">
                                      {item.display_value} {item.display_unit}
                                    </span>
                                    <span className="text-sm font-bold text-emerald-600">₵{item.price}</span>
                                  </div>
                                  {item.route_destination && (
                                    <p className="text-xs text-gray-600">Route: {item.route_destination}</p>
                                  )}
                                  {item.route_type && (
                                    <p className="text-xs text-gray-500 capitalize">Type: {item.route_type}</p>
                                  )}
                                </div>
                              );
                            }
                            // Other Policy Types (Travel, Home, Life Insurance) - Package Structure
                            else {
                              return (
                                <div key={idx} className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                                  <div className="flex items-center justify-between mb-2">
                                    <h5 className="text-sm font-bold text-gray-900">{item.product_name || `Package ${idx + 1}`}</h5>
                                    <div className="text-right">
                                      <span className="text-sm font-bold text-emerald-600">₵{item.premium}</span>
                                      {item.premium_duration && (
                                        <span className="text-xs text-gray-500 block capitalize">/{item.premium_duration}</span>
                                      )}
                                    </div>
                                  </div>
                                  {item.duration && (
                                    <p className="text-xs text-gray-600 mb-2">
                                      <span className="font-medium">Duration:</span> {item.duration}
                                    </p>
                                  )}
                                  {item.description && (
                                    <p className="text-xs text-gray-700 mb-3">{item.description}</p>
                                  )}
                                  
                                  {/* Package-specific Coverage List */}
                                  {item.insurance_coverage_list && item.insurance_coverage_list.length > 0 && (
                                    <div className="mb-3 pt-3 border-t border-blue-200">
                                      <p className="text-xs font-semibold text-gray-700 mb-2">Coverage:</p>
                                      <div className="space-y-1">
                                        {item.insurance_coverage_list.map((coverage, cIdx) => (
                                          <div key={cIdx} className="text-xs">
                                            <span className="font-medium text-gray-900">{coverage.title}</span>
                                            {coverage.limit && (
                                              <span className="text-emerald-600 ml-2">₵{coverage.limit}</span>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Package-specific Exclusions */}
                                  {item.exclusions && item.exclusions.length > 0 && (
                                    <div className="mb-2 pt-2 border-t border-blue-200">
                                      <p className="text-xs font-semibold text-red-700 mb-1">Exclusions:</p>
                                      <ul className="space-y-0.5">
                                        {item.exclusions.map((exclusion, eIdx) => (
                                          <li key={eIdx} className="text-xs text-red-600">• {exclusion}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}

                                  {/* Package-specific Eligibility */}
                                  {item.eligibility_criteria && item.eligibility_criteria.length > 0 && (
                                    <div className="pt-2 border-t border-blue-200">
                                      <p className="text-xs font-semibold text-gray-700 mb-1">Eligibility:</p>
                                      <ul className="space-y-0.5">
                                        {item.eligibility_criteria.map((criteria, crIdx) => (
                                          <li key={crIdx} className="text-xs text-gray-600">• {criteria}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              );
                            }
                          })}
                        </div>
                      </div>
                    )}

                    {/* General Coverage Information (for intercity_travel or when no package-specific coverage) */}
                    {claim.policy.insurance_coverage_list && claim.policy.insurance_coverage_list.length > 0 && 
                     (claim.policy.policy_type === "intercity_travel" || !claim.policy.coverage_durations?.[0]?.insurance_coverage_list) && (
                      <div className="mt-4 pt-4 border-t border-blue-200">
                        <p className="text-xs font-medium text-gray-600 mb-2">Coverage Details</p>
                        <div className="space-y-2">
                          {claim.policy.insurance_coverage_list.map((coverage, idx) => (
                            <div key={idx} className="p-3 bg-white rounded-lg border border-gray-200">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <p className="text-sm font-semibold text-gray-900">{coverage.title}</p>
                                  {coverage.description && (
                                    <p className="text-xs text-gray-600 mt-1">{coverage.description}</p>
                                  )}
                                </div>
                                {coverage.limit && (
                                  <span className="text-sm font-bold text-green-600 ml-2">Limit: ₵{coverage.limit}</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Insurance Coverage List */}
                    {claim.policy.insurance_coverage_list && claim.policy.insurance_coverage_list.length > 0 && (
                      <div className="mt-4">
                        <p className="text-xs font-medium text-gray-600 mb-2">Coverage Details</p>
                        <div className="space-y-2">
                          {claim.policy.insurance_coverage_list.map((coverage, idx) => (
                            <div key={idx} className="p-3 bg-white rounded-lg border border-gray-200">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <p className="text-sm font-semibold text-gray-900">{coverage.title}</p>
                                  {coverage.description && (
                                    <p className="text-xs text-gray-600 mt-1">{coverage.description}</p>
                                  )}
                                </div>
                                {coverage.limit && (
                                  <span className="text-sm font-bold text-green-600 ml-2">Limit: ₵{coverage.limit}</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Eligibility Criteria */}
                    {claim.policy.eligibility_criteria && claim.policy.eligibility_criteria.length > 0 && (
                      <div className="mt-4">
                        <p className="text-xs font-medium text-gray-600 mb-2">Eligibility Criteria</p>
                        <ul className="space-y-1">
                          {claim.policy.eligibility_criteria.map((criteria, idx) => (
                            <li key={idx} className="flex items-start text-sm text-gray-700">
                              <CheckCircle className="mr-2 mt-0.5 w-4 h-4 text-blue-600 flex-shrink-0" />
                              <span>{criteria}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Exclusions */}
                    {claim.policy.exclusions && claim.policy.exclusions.length > 0 && (
                      <div className="mt-4">
                        <p className="text-xs font-medium text-gray-600 mb-2">Exclusions</p>
                        <ul className="space-y-1">
                          {claim.policy.exclusions.map((exclusion, idx) => (
                            <li key={idx} className="flex items-start text-sm text-red-700">
                              <XCircle className="mr-2 mt-0.5 w-4 h-4 text-red-600 flex-shrink-0" />
                              <span>{exclusion}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Claim Process Steps */}
                    {claim.policy.claim_process_steps && claim.policy.claim_process_steps.length > 0 && (
                      <div className="mt-4">
                        <p className="text-xs font-medium text-gray-600 mb-2">Claim Process Steps</p>
                        <div className="space-y-3">
                          {claim.policy.claim_process_steps.map((step, idx) => (
                            <div key={idx} className="p-3 bg-white rounded-lg border border-gray-200">
                              <div className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold mr-3">
                                  {step.step_number}
                                </div>
                                <div className="flex-1">
                                  <p className="text-sm font-semibold text-gray-900">{step.title}</p>
                                  {step.description && (
                                    <p className="text-xs text-gray-600 mt-1">{step.description}</p>
                                  )}
                                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                    {step.timeframe && (
                                      <span className="flex items-center">
                                        <Clock className="mr-1 w-3 h-3" />
                                        {step.timeframe}
                                      </span>
                                    )}
                                    {step.requirements && (
                                      <span className="flex items-center">
                                        <FileText className="mr-1 w-3 h-3" />
                                        {step.requirements}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Supporting Documents */}
                {claim.supporting_documents && claim.supporting_documents.length > 0 && (
                  <div className="p-5 bg-gray-50 rounded-xl">
                    <h3 className="flex items-center mb-4 text-lg font-bold text-gray-900">
                      <FileCheck className="mr-2 w-5 h-5 text-gray-600" />
                      Supporting Documents
                    </h3>
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                      {claim.supporting_documents.map((doc, idx) => (
                        <a
                          key={idx}
                          href={doc}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all"
                        >
                          <FileText className="mr-2 w-4 h-4 text-blue-600" />
                          <span className="text-xs font-medium text-gray-700">Doc {idx + 1}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Declarations */}
                {claim.declarations && claim.declarations.length > 0 && (
                  <div className="p-5 bg-green-50 rounded-xl border border-green-100">
                    <h3 className="flex items-center mb-4 text-lg font-bold text-gray-900">
                      <CheckCircle className="mr-2 w-5 h-5 text-green-600" />
                      Declarations
                    </h3>
                    <ul className="space-y-2">
                      {claim.declarations.map((declaration, idx) => (
                        <li key={idx} className="flex items-start text-sm text-gray-700">
                          <CheckCircle className="mr-2 mt-0.5 w-4 h-4 text-green-600 flex-shrink-0" />
                          <span>{declaration}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default function ClaimsManagement() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const policySlug = searchParams.get("policy");
  const policyName = location.state?.policyName || null;
  const fromPath = location.state?.from || null;

  const [globalFilter, setGlobalFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [providerFilter, setProviderFilter] = useState("all");
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [claimsData, setClaimsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const pageSize = 10;

  useEffect(() => {
    const fetchClaims = async () => {
      setLoading(true);
      try {
        let response;
        
        // If policy slug is provided, fetch policy-specific claims
        if (policySlug) {
          response = await claimsService.getClaimsByPolicy(policySlug);
        } else {
          // Otherwise, fetch all claims
          response = await claimsService.getClaims();
        }

        // Handle response structure - check both status and data fields
        if (response.status && response.data) {
          // Handle different response structures
          let claimsArray = [];
          const data = response.data;
          
          if (Array.isArray(data)) {
            claimsArray = data;
          } else if (data?.data) {
            if (Array.isArray(data.data)) {
              claimsArray = data.data;
            } else if (data.data?.data && Array.isArray(data.data.data)) {
              claimsArray = data.data.data;
            }
          } else if (data?.status_code === 200 && data?.data) {
            if (Array.isArray(data.data)) {
              claimsArray = data.data;
            } else if (data.data?.data && Array.isArray(data.data.data)) {
              claimsArray = data.data.data;
            }
          }
          
          setClaimsData(claimsArray);
        } else {
          toast.error(response.data?.message || response.message || "Failed to fetch claims");
          setClaimsData([]);
        }
      } catch (error) {
        toast.error(error.response?.reason || "An error occurred while getting claims");
        setClaimsData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchClaims();
  }, [policySlug]);

  // Calculate stats from real data
  const stats = useMemo(() => {
    const totalClaims = claimsData.length;
    const pendingClaims = claimsData.filter((c) => c.claim_status === "pending").length;
    const receivedClaims = claimsData.filter((c) => c.claim_status === "received").length;
    const reviewingClaims = claimsData.filter((c) => c.claim_status === "reviewing").length;
    const claimedClaims = claimsData.filter((c) => c.claim_status === "claimed").length;
    const rejectedClaims = claimsData.filter((c) => c.claim_status === "rejected").length;

    // Get unique providers
    const uniqueProviders = new Set(
      claimsData
        .map((c) => c.insurance_provider?.company_name || c.insurance_provider_slug)
        .filter(Boolean)
    ).size;

    return {
      totalClaims,
      pendingClaims,
      receivedClaims,
      reviewingClaims,
      claimedClaims,
      rejectedClaims,
      uniqueProviders,
    };
  }, [claimsData]);

  // Get unique providers for filter
  const uniqueProviders = useMemo(() => {
    const providers = new Map();
    claimsData.forEach((claim) => {
      const provider = claim.insurance_provider;
      if (provider) {
        const identifier = provider.slug || provider.company_name;
        const displayName = provider.company_name || provider.slug;
        if (identifier) {
          providers.set(identifier, displayName);
        }
      }
    });
    return Array.from(providers.entries()).map(([identifier, name]) => ({ slug: identifier, name }));
  }, [claimsData]);


  const filteredClaims = useMemo(() => {
    return claimsData.filter((claim) => {
      const user = claim.user || {};
      const policy = claim.policy || {};
      const provider = claim.insurance_provider || {};
      const userName = `${user.first_name || ""} ${user.last_name || ""}`.trim().toLowerCase();
      const userEmail = user.email?.toLowerCase() || "";
      
      const matchesGlobal =
        globalFilter === "" ||
        claim.slug?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        claim.policy_slug?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        policy.policy_name?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        policy.policy_type?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        provider.company_name?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        claim.incident?.[0]?.location?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        claim.incident?.[0]?.type?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        claim.user_slug?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        userName.includes(globalFilter.toLowerCase()) ||
        userEmail.includes(globalFilter.toLowerCase());

      const matchesStatus =
        statusFilter === "all" || claim.claim_status === statusFilter;

      const providerIdentifier = provider.slug || provider.company_name || claim.insurance_provider_slug;
      const matchesProvider =
        providerFilter === "all" ||
        providerIdentifier === providerFilter;

      const matchesDateRange =
        !startDate ||
        !endDate ||
        (claim.incident?.[0]?.date &&
          parseISO(claim.incident[0].date) >= startDate &&
          parseISO(claim.incident[0].date) <= endDate);

      return matchesGlobal && matchesStatus && matchesProvider && matchesDateRange;
    });
  }, [claimsData, globalFilter, statusFilter, providerFilter, startDate, endDate]);

  const totalPages = Math.ceil(filteredClaims.length / pageSize);
  const paginatedClaims = filteredClaims.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  if (loading) {
    return <Loader fullScreen={true} text="Loading claims..." />;
  }

  return (
    <div className="p-6 min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50">
      <div className="mx-auto max-w-full">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          {/* Policy Filter Badge */}
          {policySlug && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="mb-4 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200 shadow-sm"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Shield className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-purple-700">Viewing Claims for Policy</p>
                    <p className="text-lg font-bold text-purple-900">{policyName || "Policy"}</p>
                    <p className="text-xs text-purple-600 font-mono mt-0.5">{policySlug}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {fromPath && (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => navigate(fromPath)}
                      className="flex items-center px-3 py-2 text-sm font-medium text-purple-700 bg-white rounded-lg border border-purple-200 transition-colors hover:bg-purple-50"
                      title="Go back to policies"
                    >
                      <ArrowLeft className="mr-2 w-4 h-4" />
                      Back
                    </motion.button>
                  )}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setSearchParams({});
                      navigate("/admin/insurance/claims", { replace: true });
                    }}
                    className="flex items-center px-3 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg transition-colors hover:bg-purple-700"
                    title="View all claims"
                  >
                    <X className="mr-2 w-4 h-4" />
                    View All Claims
                  </motion.button>
                </div>
              </div>
            </motion.div>
          )}

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="mb-4 lg:mb-0">
              <h1 className="mb-2 text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                {policySlug ? "Policy Claims" : "Claims Management"}
              </h1>
              <p className="text-gray-600">
                {policySlug 
                  ? `All claims for ${policyName || "this policy"}` 
                  : "Monitor and manage all insurance claims across all providers"}
              </p>
            </div>

            <div className="flex items-center space-x-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center px-4 py-2 space-x-2 bg-white rounded-lg border border-gray-200 transition-all duration-200 hover:shadow-md"
              >
                <Download className="w-4 h-4" />
                <span>Export Report</span>
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2 lg:grid-cols-4"
        >
          <motion.div
            whileHover={{ scale: 1.02, y: -2 }}
            className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm"
          >
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Claims</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalClaims}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02, y: -2 }}
            className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm"
          >
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-gray-900">{stats.pendingClaims}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02, y: -2 }}
            className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm"
          >
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Approved</p>
                <p className="text-2xl font-bold text-gray-900">{stats.claimedClaims}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02, y: -2 }}
            className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm"
          >
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Building2 className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Providers</p>
                <p className="text-2xl font-bold text-gray-900">{stats.uniqueProviders}</p>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Filters Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-6 bg-white rounded-xl border border-gray-200 shadow-sm"
        >
          <div className="p-6">
            <div className="flex flex-col mb-4 lg:flex-row lg:items-center lg:justify-between">
              <h3 className="mb-4 text-lg font-semibold text-gray-900 lg:mb-0">
                Search & Filters
              </h3>
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center px-4 py-2 space-x-2 text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200 lg:hidden"
              >
                <Filter className="w-4 h-4" />
                <span>Toggle Filters</span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${
                    showFilters ? "rotate-180" : ""
                  }`}
                />
              </motion.button>
            </div>

            <AnimatePresence>
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className={`${showFilters ? "block" : "hidden"} lg:block`}
              >
                <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder="Search claims..."
                      value={globalFilter}
                      onChange={(e) => setGlobalFilter(e.target.value)}
                      className="py-2 pr-4 pl-10 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="received">Received</option>
                    <option value="reviewing">Reviewing</option>
                    <option value="claimed">Claimed</option>
                    <option value="rejected">Rejected</option>
                  </select>

                  <select
                    value={providerFilter}
                    onChange={(e) => setProviderFilter(e.target.value)}
                    className="px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Providers</option>
                    {uniqueProviders.map((provider) => (
                      <option key={provider.slug} value={provider.slug}>
                        {provider.name}
                      </option>
                    ))}
                  </select>

                  <div className="col-span-1">
                    <DatePicker
                      selectsRange={true}
                      startDate={startDate}
                      endDate={endDate}
                      onChange={(update) => setDateRange(update)}
                      placeholderText="Select date range"
                      className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Claims Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm"
        >
          {filteredClaims.length === 0 ? (
            <div className="py-20">
              <EmptyState
                icon={FileText}
                title="No Claims Found"
                description="No claims match your current filters. Try adjusting your search criteria."
              />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-gray-50 to-blue-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-sm font-semibold text-left text-gray-700 whitespace-nowrap">Claim ID</th>
                      <th className="px-6 py-4 text-sm font-semibold text-left text-gray-700 whitespace-nowrap">User</th>
                      <th className="px-6 py-4 text-sm font-semibold text-left text-gray-700 whitespace-nowrap">Provider</th>
                      <th className="px-6 py-4 text-sm font-semibold text-left text-gray-700 whitespace-nowrap">Policy</th>
                      <th className="px-6 py-4 text-sm font-semibold text-left text-gray-700 whitespace-nowrap">Incident Date</th>
                      <th className="px-6 py-4 text-sm font-semibold text-left text-gray-700 whitespace-nowrap">Location</th>
                      <th className="px-6 py-4 text-sm font-semibold text-left text-gray-700 whitespace-nowrap">Type</th>
                      <th className="px-6 py-4 text-sm font-semibold text-left text-gray-700 whitespace-nowrap">Status</th>
                      <th className="px-6 py-4 text-sm font-semibold text-left text-gray-700 whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <AnimatePresence>
                      {paginatedClaims.map((claim, index) => {
                        const incident = claim.incident?.[0] || {};
                        const user = claim.user || {};
                        const policy = claim.policy || {};
                        const provider = claim.insurance_provider || {};
                        const userName = `${user.first_name || ""} ${user.last_name || ""}`.trim() || "N/A";
                        const currentStatus = claim.claim_status || "pending";

                        return (
                          <motion.tr
                            key={claim.slug}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="transition-colors hover:bg-blue-50/50"
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <motion.div
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                className="inline-flex items-center px-3 py-1.5 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200"
                              >
                                <div className="flex items-center space-x-2">
                                  <div className="p-1 bg-blue-100 rounded">
                                    <FileText className="w-3 h-3 text-blue-600" />
                                  </div>
                                  <span className="font-mono text-xs font-semibold text-blue-700">
                                    {claim.slug?.slice(0, 8).toUpperCase()}
                                  </span>
                                </div>
                              </motion.div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm">
                                <div className="flex items-center">
                                  {/* <User className="mr-2 w-4 h-4 text-gray-400 flex-shrink-0" /> */}
                                  <p className="font-semibold text-gray-900 truncate">{userName}</p>
                                </div>
                                <p className="text-xs text-gray-500 truncate">{user.email || "N/A"}</p>
                                {/* <p className="text-xs text-gray-400 font-mono truncate">{claim.user_slug?.slice(0, 8)}...</p> */}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center text-sm">
                                {provider.company_logo && (
                                  <img 
                                    src={provider.company_logo} 
                                    alt={provider.company_name}
                                    className="mr-2 w-6 h-6 rounded object-cover flex-shrink-0"
                                  />
                                )}
                                <Building2 className={`mr-2 w-4 h-4 text-gray-400 flex-shrink-0 ${provider.company_logo ? 'hidden' : ''}`} />
                                <div className="min-w-0">
                                  <p className="font-semibold text-gray-900 truncate" title={provider.company_name}>
                                    {provider.company_name || "N/A"}
                                  </p>
                                  {provider.location && (
                                    <p className="text-xs text-gray-500 truncate">{provider.location}, {provider.region}</p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm">
                                <p className="font-semibold text-gray-900 truncate" title={policy.policy_name}>
                                  {policy.policy_name || "N/A"}
                                </p>
                                <p className="text-xs text-gray-500 capitalize truncate">
                                  {policy.policy_type?.replace(/_/g, " ") || "N/A"}
                                </p>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                              {incident.date
                                ? format(parseISO(incident.date), "MMM dd, yyyy")
                                : "N/A"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center text-sm text-gray-700">
                                <MapPin className="mr-1 w-4 h-4 text-gray-400 flex-shrink-0" />
                                <span className="truncate">{incident.location || "N/A"}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800">
                                {incident.type || "N/A"}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <StatusBadge status={currentStatus} />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => {
                                  setSelectedClaim(claim);
                                  setShowDetailsModal(true);
                                }}
                                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg transition-colors hover:bg-blue-100"
                              >
                                <Eye className="mr-1 w-4 h-4" />
                                View
                              </motion.button>
                            </td>
                          </motion.tr>
                        );
                      })}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                  <div className="flex flex-col items-center justify-between lg:flex-row">
                    <div className="flex items-center mb-4 space-x-2 lg:mb-0">
                      <span className="text-sm text-gray-700">
                        Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, filteredClaims.length)} of {filteredClaims.length} claims
                      </span>
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        className="p-2 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                      >
                        <ChevronsLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="p-2 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>

                      <span className="px-4 py-2 text-sm text-gray-700">
                        Page {currentPage} of {totalPages || 1}
                      </span>

                      <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage >= totalPages}
                        className="p-2 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage >= totalPages}
                        className="p-2 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                      >
                        <ChevronsRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </motion.div>
      </div>

      {/* Claim Details Modal */}
      <ClaimDetailsModal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedClaim(null);
        }}
        claim={selectedClaim}
      />

    </div>
  );
}

